import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import Field from "../components/Field";
import { useAuth } from "../context/AuthContext";
import { validateEmail, validatePassword } from "../api/validators";
import { finishLoginWithReturn, getReturnUrlFromQuery } from "../api/playTicket";
import OAuthDivider from "../components/OAuthDivider";
import { CAPABILITIES } from "../api/authGateway";
import { Button } from "../components/ui";
import usePageTitle from "../hooks/usePageTitle";
import logo from "../assets/logo.png";
import YandexLoginButton from "../components/YandexLoginButton";
import "../components/AuthForm.css";

/** Вход. POST login → токен в localStorage → редирект на главную (или откуда пришёл). */
export default function LoginPage() {
  usePageTitle("Вход");
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // куда вернуть после входа (если кинул ProtectedRoute) — иначе в кабинет
  const from = location.state?.from || "/app";

  // Завершение входа: если есть ?return на инвайт-ссылку студии/плеера —
  // finishLoginWithReturn выпустит SSO-ticket и уйдёт туда с #ticket; иначе
  // обычная навигация в кабинет. Общий код с OAuth-callback'ами (см. playTicket).
  const finishLogin = () =>
    finishLoginWithReturn(() => navigate(from, { replace: true }));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // уже залогинен — если пришёл с ?return (инвайт-ссылка) выпускаем ticket и
  // уходим туда; иначе в кабинет. Важно для случая «вошёл раньше, потом
  // открыл инвайт-ссылку» — не застреваем в /app.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (getReturnUrlFromQuery()) { finishLogin(); return; }
    navigate("/app", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    const fieldErrors = {
      email: validateEmail(email),
      password: validatePassword(password),
    };
    setErrors(fieldErrors);
    if (fieldErrors.email || fieldErrors.password) return;

    setSubmitting(true);
    try {
      await login({ email: email.trim(), password });
      await finishLogin();
    } catch (err) {
      setFormError(err.message || "Не удалось войти. Попробуйте ещё раз.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="rb-auth">
        <form className="rb-auth__card" onSubmit={handleSubmit} noValidate>
          <img src={logo} alt="Рублокс" className="rb-auth__logo" />
          <h1 className="rb-auth__title">Вход в Рублокс</h1>
          <p className="rb-auth__subtitle">
            Единый аккаунт для платформы и онлайн-школы «Майнкрафтия»
          </p>

          {formError && <div className="rb-auth__error">{formError}</div>}

          <Field
            id="login-email"
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="you@example.com"
            error={errors.email}
            autoComplete="email"
            disabled={submitting}
            inputMode="email"
          />

          <Field
            id="login-password"
            label="Пароль"
            type={showPw ? "text" : "password"}
            value={password}
            onChange={setPassword}
            placeholder="Ваш пароль"
            error={errors.password}
            autoComplete="current-password"
            disabled={submitting}
          />
          <button
            type="button"
            className="rb-field__toggle-pw"
            onClick={() => setShowPw((v) => !v)}
          >
            {showPw ? "Скрыть пароль" : "Показать пароль"}
          </button>

          {/* loading вместо подмены текста: «Входим…» меняло ширину кнопки. */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={submitting}
            className="rb-auth__submit"
          >
            Войти
          </Button>

          {/* OAuth показываем, только если провайдер реально работает.
              При IDENTITY=v2 обе кнопки скрыты: Google в схеме v2 нет
              вовсе, а Яндекс есть, но на сервере не настроен — нажатие
              вело бы к ошибке, которую человек не может исправить.
              Когда Яндекс настроят, достаточно вернуть флаг в CAPABILITIES. */}
          {/* Кнопки Google нет: решение Мина 25.09.2026 — убрана со всех
              сервисов. Раньше здесь стояла заглушка-объяснение «вход через
              Google недоступен». */}
          <OAuthDivider />
          {CAPABILITIES.yandexOAuth && <YandexLoginButton disabled={submitting} />}

          <div className="rb-auth__switch">
            Нет аккаунта? <Link to="/signup">Зарегистрироваться</Link>
          </div>
        </form>
      </div>
    </Layout>
  );
}
